// Mobile UI renders for the Kinly marketing page.
// All visuals are inline SVG/Tailwind — no images, infinitely sharp.
// Kinly is iOS/Android only; these illustrate the mobile app on a marketing site.
import { Mic, Check, MapPin, Users, ChevronRight, Sparkles, Clock } from "lucide-react";

/* ────────────────────────────────────────────────────────────────────
 *  Shared phone frame (for the hero showcase only)
 * ──────────────────────────────────────────────────────────────────── */
export function PhoneFrame({ children, className = "", w = 320 }) {
  return (
    <div className={`relative mx-auto ${className}`} style={{ maxWidth: w }}>
      <div className="absolute -inset-8 bg-gradient-to-br from-kinly-peach via-transparent to-transparent blur-3xl opacity-80 pointer-events-none" />
      <div className="relative rounded-[2.4rem] bg-kinly-ink p-2 shadow-[0_25px_55px_-15px_rgba(0,0,0,0.18)]">
        <div className="absolute top-2 left-1/2 -translate-x-1/2 w-24 h-4 bg-kinly-ink rounded-b-2xl z-10" />
        <div className="rounded-[2rem] bg-kinly-bg overflow-hidden aspect-[9/19.5] relative">
          {/* Soft peach atmospheric glow inside the screen */}
          <div className="absolute top-0 right-0 w-3/4 h-2/5 pointer-events-none" style={{
            background: "radial-gradient(ellipse at 80% 20%, #FBE9DF 0%, transparent 70%)",
          }} />
          <div className="relative h-full">{children}</div>
        </div>
      </div>
    </div>
  );
}

/* Status bar shared by phone screens */
function StatusBar({ time = "7:02" }) {
  return (
    <div className="flex items-center justify-between px-5 pt-3.5 pb-1">
      <span className="text-[10px] font-semibold text-kinly-ink">{time}</span>
      <span className="text-[10px] font-semibold text-kinly-ink2">●●●●</span>
    </div>
  );
}

/* Small avatar pill */
function Avatar({ name, color = "bg-kinly-ink", size = "w-6 h-6", text = "text-[10px]" }) {
  return (
    <div className={`${size} ${color} rounded-full flex items-center justify-center flex-shrink-0`}>
      <span className={`text-white ${text} font-semibold`}>{name}</span>
    </div>
  );
}

/* ────────────────────────────────────────────────────────────────────
 *  1. Talk to Kinly — transcription → AI reasoning → event created
 * ──────────────────────────────────────────────────────────────────── */
export function TalkToKinlyRender() {
  return (
    <PhoneFrame w={340}>
      <StatusBar />
      <div className="px-5 pt-2 pb-3 border-b border-kinly-line">
        <div className="flex items-center gap-2">
          <div className="w-5 h-5 rounded-full bg-kinly-coral flex items-center justify-center">
            <span className="font-kinly text-white font-medium text-[11px] leading-none -mt-px">k</span>
          </div>
          <p className="text-[10px] font-medium uppercase tracking-[0.18em] text-kinly-ink2">Kinly</p>
        </div>
        <p className="font-kinly font-medium text-[15px] text-kinly-ink mt-1.5">Add to calendar</p>
      </div>

      {/* Step 1 — voice + transcript */}
      <div className="px-5 pt-4">
        <div className="flex items-center gap-2 mb-2.5">
          <div className="w-7 h-7 rounded-full bg-kinly-coral flex items-center justify-center">
            <Mic className="w-3.5 h-3.5 text-white" strokeWidth={2.5} />
          </div>
          <div className="flex items-end gap-[2px] h-5">
            {[6,12,18,9,15,7,13,5,10,8,14,6].map((h,i) => (
              <span key={i} className="w-[3px] rounded-full bg-kinly-coral" style={{ height: `${h}px` }} />
            ))}
          </div>
          <span className="text-[10px] font-medium text-kinly-ink2 ml-1">0:04</span>
        </div>
        <p className="text-[13px] text-kinly-ink leading-[1.45]">
          "Lily piano every Tuesday at four, Mrs Smith, until end of term."
        </p>
      </div>

      {/* Step 2 — AI parsing */}
      <div className="mx-5 mt-4 mb-4 rounded-xl bg-kinly-bg border border-kinly-line p-3">
        <div className="flex items-center gap-1.5 mb-2">
          <Sparkles className="w-3 h-3 text-kinly-coral" strokeWidth={2.5} />
          <p className="text-[10px] font-semibold uppercase tracking-[0.12em] text-kinly-coral">Kinly understood</p>
        </div>
        <div className="flex flex-wrap gap-1">
          {[
            ["who", "Lily"],
            ["activity", "Piano"],
            ["every", "Tuesday"],
            ["at", "16:00"],
            ["with", "Mrs Smith"],
            ["until", "term-end"],
          ].map(([k, v]) => (
            <span key={k} className="text-[10px] bg-white border border-kinly-line rounded-md px-1.5 py-0.5">
              <span className="text-kinly-ink2">{k}:</span> <span className="text-kinly-ink font-semibold">{v}</span>
            </span>
          ))}
        </div>
      </div>

      {/* Step 3 — event created */}
      <div className="mx-5 rounded-xl bg-white border border-kinly-coral/40 p-3 shadow-[0_2px_0_rgba(224,120,86,0.08)]">
        <div className="flex items-start gap-2.5">
          <div className="w-9 h-9 rounded-full bg-kinly-coral/12 flex items-center justify-center flex-shrink-0">
            <Check className="w-4 h-4 text-kinly-coral" strokeWidth={2.75} />
          </div>
          <div className="flex-1">
            <p className="text-[10px] font-semibold uppercase tracking-[0.1em] text-kinly-coral mb-0.5">Created</p>
            <p className="text-[13px] font-semibold text-kinly-ink leading-tight">Piano — Lily</p>
            <p className="text-[11px] text-kinly-ink2 mt-0.5">Every Tue · 16:00 · Mrs Smith</p>
            <p className="text-[10px] text-kinly-ink2 mt-0.5">12 weeks · ends 28 Mar</p>
          </div>
        </div>
      </div>
      <p className="text-center text-[10px] text-kinly-ink2 mt-3">Four seconds. Not ninety.</p>
    </PhoneFrame>
  );
}

/* ────────────────────────────────────────────────────────────────────
 *  2. Calendar — week at a glance
 * ──────────────────────────────────────────────────────────────────── */
export function CalendarRender() {
  const days = ["M", "T", "W", "T", "F", "S", "S"];
  const nums = [9, 10, 11, 12, 13, 14, 15];
  const todayIdx = 1;
  const events = [
    { day: 0, who: "L", color: "bg-kinly-coral", label: "Piano · 16:00" },
    { day: 1, who: "S", color: "bg-kinly-ink", label: "Yoga · 18:30" },
    { day: 2, who: "—", color: "bg-amber-700", label: "Bin day" },
    { day: 3, who: "L", color: "bg-stone-500", label: "Swim meet" },
    { day: 4, who: "S", color: "bg-kinly-coral", label: "Date night" },
  ];
  return (
    <div className="rounded-2xl bg-white border border-kinly-line p-5 shadow-[0_2px_0_rgba(0,0,0,0.02)] max-w-sm mx-auto" data-testid="kinly-render-calendar">
      <div className="flex items-center justify-between mb-4">
        <div>
          <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-kinly-ink2">This week</p>
          <p className="text-[17px] font-bold text-kinly-ink tracking-tight">March 9 – 15</p>
        </div>
        <div className="text-[10px] font-medium text-kinly-ink2 bg-kinly-bg border border-kinly-line px-2 py-1 rounded-md">Week 11</div>
      </div>

      <div className="grid grid-cols-7 gap-1 mb-3">
        {days.map((d, i) => (
          <div key={i} className="text-center">
            <p className="text-[9px] font-semibold text-kinly-ink2 uppercase tracking-wider mb-1">{d}</p>
            <div className={`mx-auto w-7 h-7 rounded-full flex items-center justify-center text-[11px] font-semibold ${
              i === todayIdx ? "bg-kinly-ink text-white" : "text-kinly-ink"
            }`}>{nums[i]}</div>
          </div>
        ))}
      </div>

      <div className="space-y-1.5">
        {events.map((e, i) => (
          <div key={i} className="flex items-center gap-2.5 rounded-lg bg-kinly-bg border border-kinly-line px-2.5 py-2">
            <div className={`w-1 h-7 rounded-full ${e.color}`} />
            <Avatar name={e.who} color={e.color} size="w-5 h-5" text="text-[9px]" />
            <p className="text-[11px] text-kinly-ink font-medium flex-1 truncate">{e.label}</p>
            <ChevronRight className="w-3 h-3 text-kinly-ink2" />
          </div>
        ))}
      </div>
    </div>
  );
}

/* ────────────────────────────────────────────────────────────────────
 *  3. Poll — Dinner Friday?
 * ──────────────────────────────────────────────────────────────────── */
export function PollRender() {
  const options = [
    { name: "Spur",     votes: 2, voters: [{n:"S",c:"bg-kinly-ink"}, {n:"L",c:"bg-kinly-coral"}], pct: 50, lead: true },
    { name: "Tashas",   votes: 1, voters: [{n:"M",c:"bg-stone-500"}], pct: 25 },
    { name: "Mike's",   votes: 0, voters: [], pct: 0 },
    { name: "Wildcard · Sushi",  votes: 1, voters: [{n:"D",c:"bg-amber-700"}], pct: 25 },
  ];
  return (
    <div className="rounded-2xl bg-white border border-kinly-line p-5 max-w-sm mx-auto" data-testid="kinly-render-poll">
      <div className="flex items-start justify-between mb-4">
        <div>
          <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-kinly-coral mb-1">Family poll · Kinly suggested</p>
          <p className="text-[17px] font-bold text-kinly-ink tracking-tight leading-tight">Dinner Friday?</p>
        </div>
        <div className="flex items-center gap-1 text-[10px] font-medium text-kinly-ink2">
          <Clock className="w-3 h-3" /> 5pm
        </div>
      </div>

      <div className="space-y-2 mb-3">
        {options.map((o, i) => (
          <div key={i} className="relative rounded-lg border border-kinly-line bg-kinly-bg overflow-hidden">
            <div
              className={`absolute inset-y-0 left-0 ${o.lead ? "bg-kinly-coral/15" : "bg-kinly-ink/5"}`}
              style={{ width: `${o.pct}%` }}
            />
            <div className="relative flex items-center justify-between px-3 py-2.5">
              <div className="flex items-center gap-2">
                <span className={`text-[12px] font-semibold ${o.lead ? "text-kinly-coral" : "text-kinly-ink"}`}>{o.name}</span>
                {o.lead && <span className="text-[8px] font-bold uppercase tracking-wider text-kinly-coral bg-white border border-kinly-coral/30 rounded px-1 py-px">Leading</span>}
              </div>
              <div className="flex items-center gap-1.5">
                <div className="flex -space-x-1.5">
                  {o.voters.map((v, j) => (
                    <Avatar key={j} name={v.n} color={v.c} size="w-4 h-4" text="text-[8px]" />
                  ))}
                </div>
                <span className="text-[11px] font-semibold text-kinly-ink2 w-6 text-right">{o.votes}</span>
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="flex items-center justify-between pt-3 border-t border-kinly-line">
        <div className="flex items-center gap-1.5">
          <Users className="w-3 h-3 text-kinly-ink2" />
          <span className="text-[10px] text-kinly-ink2 font-medium">4 of 5 voted</span>
        </div>
        <span className="text-[10px] font-semibold text-kinly-coral">Closes in 2h</span>
      </div>
    </div>
  );
}

/* ────────────────────────────────────────────────────────────────────
 *  4. Chores — streaks, no nagging
 * ──────────────────────────────────────────────────────────────────── */
export function ChoresRender() {
  const tasks = [
    { who: "Lily",  init: "L", color: "bg-kinly-coral", task: "Empty dishwasher", streak: 5,  status: "done" },
    { who: "Liam",  init: "L", color: "bg-stone-500",      task: "Walk Max",         streak: 12, status: "approve" },
    { who: "Sarah", init: "S", color: "bg-kinly-ink",     task: "Friday shop",      streak: 3,  status: "todo" },
  ];
  return (
    <div className="rounded-2xl bg-white border border-kinly-line p-5 max-w-sm mx-auto" data-testid="kinly-render-chores">
      <div className="flex items-center justify-between mb-4">
        <p className="text-[17px] font-bold text-kinly-ink tracking-tight">Today's chores</p>
        <span className="text-[10px] font-semibold text-kinly-coral uppercase tracking-wider">3 of 3 assigned</span>
      </div>
      <div className="space-y-2">
        {tasks.map((t, i) => (
          <div key={i} className="flex items-center gap-3 rounded-xl bg-kinly-bg border border-kinly-line p-3">
            <Avatar name={t.init} color={t.color} size="w-8 h-8" text="text-[11px]" />
            <div className="flex-1 min-w-0">
              <p className="text-[12px] font-semibold text-kinly-ink leading-tight">{t.task}</p>
              <p className="text-[10px] text-kinly-ink2 mt-0.5">{t.who} · streak {t.streak}</p>
            </div>
            {t.status === "done" && (
              <span className="text-[10px] font-bold uppercase tracking-wider text-kinly-coral bg-kinly-coral/10 border border-kinly-coral/30 rounded-full px-2 py-1">Done</span>
            )}
            {t.status === "approve" && (
              <button className="text-[10px] font-bold uppercase tracking-wider text-white bg-kinly-coral rounded-full px-2.5 py-1">Approve</button>
            )}
            {t.status === "todo" && (
              <button className="text-[10px] font-bold uppercase tracking-wider text-kinly-ink bg-white border border-kinly-line rounded-full px-2.5 py-1">Tap done</button>
            )}
          </div>
        ))}
      </div>
      <div className="mt-3 pt-3 border-t border-kinly-line flex items-center justify-between">
        <p className="text-[10px] text-kinly-ink2">Kids see the streak. You see the lift.</p>
        <span className="text-[10px] font-semibold text-kinly-ink">⚡ +28 stars this week</span>
      </div>
    </div>
  );
}

/* ────────────────────────────────────────────────────────────────────
 *  5. Meals + auto shopping list
 * ──────────────────────────────────────────────────────────────────── */
export function MealsRender() {
  const meals = [
    { d: "Mon", m: "Spaghetti bolognese" },
    { d: "Tue", m: "Chicken stir-fry" },
    { d: "Wed", m: "Tacos · kids' fav" },
    { d: "Thu", m: "Sheet-pan salmon" },
    { d: "Fri", m: "Out — see poll" },
  ];
  const list = [
    { item: "Mince beef", qty: "500 g", done: true },
    { item: "Tortilla wraps", qty: "× 12" },
    { item: "Salmon fillets", qty: "× 4" },
    { item: "Bell peppers", qty: "× 3" },
    { item: "Pasta", qty: "1 box", done: true },
    { item: "Sour cream", qty: "1 tub" },
  ];
  return (
    <div className="grid grid-cols-2 gap-3 max-w-md mx-auto" data-testid="kinly-render-meals">
      <div className="rounded-2xl bg-white border border-kinly-line p-4">
        <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-kinly-ink2 mb-3">This week's meals</p>
        <div className="space-y-1.5">
          {meals.map((m, i) => (
            <div key={i} className="flex items-baseline gap-2">
              <span className="text-[9px] font-bold text-kinly-coral uppercase tracking-wider w-7">{m.d}</span>
              <span className="text-[11px] text-kinly-ink leading-tight">{m.m}</span>
            </div>
          ))}
        </div>
      </div>
      <div className="rounded-2xl bg-kinly-ink text-white p-4">
        <div className="flex items-center gap-1.5 mb-3">
          <Sparkles className="w-3 h-3 text-kinly-coral2" strokeWidth={2.5} />
          <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-kinly-coral2">Auto shopping list</p>
        </div>
        <div className="space-y-1.5">
          {list.map((l, i) => (
            <div key={i} className="flex items-center gap-2">
              <div className={`w-3 h-3 rounded border ${l.done ? "bg-kinly-coral2 border-kinly-coral2" : "border-white/30"} flex items-center justify-center flex-shrink-0`}>
                {l.done && <Check className="w-2 h-2 text-kinly-ink" strokeWidth={3.5} />}
              </div>
              <span className={`text-[10px] flex-1 ${l.done ? "line-through text-white/40" : "text-white"}`}>{l.item}</span>
              <span className="text-[9px] text-white/50">{l.qty}</span>
            </div>
          ))}
        </div>
        <p className="text-[9px] text-white/40 mt-3 pt-3 border-t border-white/10">Pulled from this week's meal plan.</p>
      </div>
    </div>
  );
}

/* ────────────────────────────────────────────────────────────────────
 *  6. FindMe — stylised map
 * ──────────────────────────────────────────────────────────────────── */
export function FindMeRender() {
  return (
    <div className="rounded-2xl bg-white border border-kinly-line overflow-hidden max-w-sm mx-auto" data-testid="kinly-render-findme">
      <div className="relative h-44 bg-kinly-bg">
        {/* Abstract streets via SVG */}
        <svg viewBox="0 0 320 180" className="absolute inset-0 w-full h-full" preserveAspectRatio="none">
          <defs>
            <pattern id="grid" width="40" height="40" patternUnits="userSpaceOnUse">
              <path d="M 40 0 L 0 0 0 40" fill="none" stroke="#E8E1D5" strokeWidth="1" />
            </pattern>
          </defs>
          <rect width="320" height="180" fill="url(#grid)" />
          {/* Main roads */}
          <path d="M -10 60 Q 80 65, 160 55 T 330 50" fill="none" stroke="#E8E1D5" strokeWidth="8" strokeLinecap="round" />
          <path d="M 90 -10 Q 95 80, 105 100 T 110 200" fill="none" stroke="#E8E1D5" strokeWidth="8" strokeLinecap="round" />
          <path d="M -10 130 L 330 125" fill="none" stroke="#E8E1D5" strokeWidth="6" strokeLinecap="round" />
          <path d="M 220 -10 L 215 200" fill="none" stroke="#E8E1D5" strokeWidth="6" strokeLinecap="round" />
          {/* Parks / blocks */}
          <rect x="130" y="80" width="60" height="35" rx="4" fill="#E07856" opacity="0.10" />
          <rect x="240" y="140" width="50" height="30" rx="4" fill="#2C2926" opacity="0.04" />
        </svg>
        {/* Family dots */}
        <div className="absolute" style={{ left: "22%", top: "30%" }}>
          <div className="relative">
            <span className="absolute inset-0 rounded-full bg-kinly-ink/20 animate-ping" style={{ width: 28, height: 28, left: -4, top: -4 }} />
            <Avatar name="S" color="bg-kinly-ink" size="w-5 h-5" text="text-[9px]" />
          </div>
        </div>
        <div className="absolute" style={{ left: "62%", top: "55%" }}>
          <div className="relative">
            <span className="absolute inset-0 rounded-full bg-kinly-coral/30 animate-pulse" style={{ width: 28, height: 28, left: -4, top: -4 }} />
            <Avatar name="L" color="bg-kinly-coral" size="w-5 h-5" text="text-[9px]" />
          </div>
        </div>
        <div className="absolute" style={{ left: "78%", top: "20%" }}>
          <Avatar name="M" color="bg-stone-500" size="w-5 h-5" text="text-[9px]" />
        </div>
      </div>
      {/* Bottom sheet */}
      <div className="p-4 space-y-2">
        <div className="flex items-center justify-between mb-2">
          <p className="text-[14px] font-bold text-kinly-ink tracking-tight">FindMe</p>
          <span className="text-[9px] font-semibold uppercase tracking-wider text-kinly-coral">All home by 18:30</span>
        </div>
        {[
          { n: "Sarah",  c: "bg-kinly-ink",     init: "S", loc: "Sandton City",   ago: "4 min ago" },
          { n: "Liam",   c: "bg-kinly-coral", init: "L", loc: "Crawford School", ago: "12 min ago" },
          { n: "Mike",   c: "bg-stone-500",      init: "M", loc: "Work · Rosebank", ago: "1 min ago" },
        ].map((p, i) => (
          <div key={i} className="flex items-center gap-2.5">
            <Avatar name={p.init} color={p.c} size="w-6 h-6" text="text-[10px]" />
            <div className="flex-1 min-w-0">
              <p className="text-[11px] font-semibold text-kinly-ink leading-tight">{p.n}</p>
              <p className="text-[9px] text-kinly-ink2 flex items-center gap-1"><MapPin className="w-2.5 h-2.5" />{p.loc}</p>
            </div>
            <span className="text-[9px] text-kinly-ink2">{p.ago}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ────────────────────────────────────────────────────────────────────
 *  7. Moments — photo timeline tied to events
 * ──────────────────────────────────────────────────────────────────── */
export function MomentsRender() {
  // Each moment hints at its content via a silhouetted SVG over a gradient — feels photographic without using real images
  const moments = [
    {
      tag: "Sat 14", title: "Liam's swim meet", count: 12,
      hue: "from-sky-300 to-blue-500",
      svg: (
        <svg viewBox="0 0 60 80" className="absolute inset-0 w-full h-full opacity-90" preserveAspectRatio="xMidYMid slice">
          <path d="M 0 55 Q 15 50 30 55 T 60 55 L 60 80 L 0 80 Z" fill="white" opacity="0.25" />
          <path d="M 0 62 Q 15 58 30 62 T 60 62 L 60 80 L 0 80 Z" fill="white" opacity="0.2" />
          <circle cx="34" cy="40" r="4" fill="white" opacity="0.9" />
          <path d="M 28 46 Q 34 50 40 46 L 42 54 Q 34 56 26 54 Z" fill="white" opacity="0.85" />
          <path d="M 22 48 L 30 50 M 38 50 L 46 48" stroke="white" strokeWidth="1.5" strokeLinecap="round" opacity="0.7" />
        </svg>
      ),
    },
    {
      tag: "Sun 15", title: "Sunday lunch · Nan", count: 7,
      hue: "from-amber-200 to-orange-400",
      svg: (
        <svg viewBox="0 0 60 80" className="absolute inset-0 w-full h-full opacity-90" preserveAspectRatio="xMidYMid slice">
          <ellipse cx="30" cy="48" rx="18" ry="4" fill="white" opacity="0.25" />
          <circle cx="30" cy="42" r="14" fill="white" opacity="0.55" />
          <circle cx="30" cy="42" r="9" fill="white" opacity="0.75" />
          <circle cx="22" cy="38" r="2" fill="#2C2926" opacity="0.4" />
          <circle cx="30" cy="36" r="2" fill="#2C2926" opacity="0.4" />
          <circle cx="36" cy="40" r="2" fill="#2C2926" opacity="0.4" />
        </svg>
      ),
    },
    {
      tag: "Tue 17", title: "Lily piano recital", count: 9,
      hue: "from-kinly-coral/30 to-kinly-coral/60",
      svg: (
        <svg viewBox="0 0 60 80" className="absolute inset-0 w-full h-full opacity-90" preserveAspectRatio="xMidYMid slice">
          <rect x="10" y="40" width="40" height="20" fill="white" opacity="0.5" />
          <g fill="#2C2926" opacity="0.55">
            <rect x="14" y="40" width="2.5" height="13" /><rect x="20" y="40" width="2.5" height="13" />
            <rect x="26" y="40" width="2.5" height="13" /><rect x="32" y="40" width="2.5" height="13" />
            <rect x="38" y="40" width="2.5" height="13" /><rect x="44" y="40" width="2.5" height="13" />
          </g>
          <circle cx="44" cy="28" r="3" fill="white" />
          <path d="M 44 28 L 44 18 L 48 17" stroke="white" strokeWidth="1.5" fill="none" />
        </svg>
      ),
    },
    {
      tag: "Sat 21", title: "Hike · Magaliesberg", count: 23,
      hue: "from-amber-100 to-orange-300",
      svg: (
        <svg viewBox="0 0 60 80" className="absolute inset-0 w-full h-full opacity-90" preserveAspectRatio="xMidYMid slice">
          <path d="M 0 60 L 18 38 L 28 50 L 42 30 L 60 55 L 60 80 L 0 80 Z" fill="white" opacity="0.35" />
          <path d="M 0 65 L 14 50 L 24 58 L 38 42 L 60 62 L 60 80 L 0 80 Z" fill="white" opacity="0.55" />
          <circle cx="46" cy="22" r="4" fill="white" opacity="0.9" />
        </svg>
      ),
    },
  ];
  return (
    <div className="rounded-2xl bg-white border border-kinly-line p-5 max-w-md mx-auto" data-testid="kinly-render-moments">
      <div className="flex items-center justify-between mb-4">
        <div>
          <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-kinly-ink2">Moments</p>
          <p className="text-[17px] font-bold text-kinly-ink tracking-tight">This month</p>
        </div>
        <span className="text-[10px] font-semibold text-kinly-coral">51 photos · auto-tagged</span>
      </div>
      <div className="flex gap-2.5 overflow-hidden">
        {moments.map((m, i) => (
          <div key={i} className="flex-1 min-w-0">
            <div className={`aspect-[3/4] rounded-xl bg-gradient-to-br ${m.hue} mb-2 relative overflow-hidden`}>
              {m.svg}
              <div className="absolute inset-0 bg-gradient-to-t from-black/30 via-transparent to-transparent" />
              <span className="absolute top-1.5 left-1.5 text-[8px] font-bold uppercase tracking-wider text-white bg-black/45 backdrop-blur-sm rounded px-1.5 py-0.5 z-10">
                {m.count}
              </span>
            </div>
            <p className="text-[9px] font-bold uppercase tracking-wider text-kinly-coral">{m.tag}</p>
            <p className="text-[10px] font-semibold text-kinly-ink leading-tight mt-0.5 line-clamp-2">{m.title}</p>
          </div>
        ))}
      </div>
      <p className="text-[10px] text-kinly-ink2 mt-4 pt-3 border-t border-kinly-line">Photos tie to events. A year later, you'll still find them.</p>
    </div>
  );
}
